import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/vue-query";
import axios from "axios";
import route from "ziggy-js";
import { toast } from "@/SharedComponents/Toaster";
import { useI18n } from "vue-i18n";

export const QUERY_KEY = ["notifications"];
const QUERY_KEY_UNREAD_COUNT = ["notifications", "unread_count"];
const SETTINGS_QUERY_KEY = ["notifications_settings"];

async function fetchUnreadCount() {
    const { data } = await axios.get(route("api.notifications.unread.count"));
    return data.unread_count;
}

export function useUnreadCount() {
    return useQuery({
        queryKey: QUERY_KEY_UNREAD_COUNT,
        queryFn: fetchUnreadCount,
        refetchOnMount: true,
    });
}

async function fetchNotifications({ pageParam = 0 }) {
    const { data } = await axios.get(
        route("api.notifications.all", {
            cursor: pageParam,
        })
    );
    return data;
}

export function useNotifications() {
    return useInfiniteQuery({
        queryKey: QUERY_KEY,
        queryFn: fetchNotifications,
        initialPageParam: 0,
        getNextPageParam: (lastPage) => lastPage.meta.next_cursor,
        refetchOnMount: true,
    });
}

export function useMarkAsRead() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (notification) =>
            axios.patch(route("api.notifications.read", notification)),
        onMutate: async (notification) => {
            await queryClient.cancelQueries({ queryKey: QUERY_KEY });

            const previousNotifications = queryClient.getQueryData(QUERY_KEY);

            queryClient.setQueryData(QUERY_KEY, (old) => {
                return {
                    ...old,
                    pages: old.pages.map((page) => ({
                        ...page,
                        data: page.data.map((n) =>
                            n.id === notification
                                ? {
                                      ...n,
                                      read_at: new Date().toISOString(),
                                  }
                                : n
                        ),
                    })),
                };
            });

            return { previousNotifications };
        },
        onError: (err, _, context) => {
            queryClient.setQueryData(QUERY_KEY, context.previousNotifications);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        },
    });
}

export function useMarkAllAsRead() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => axios.post(route("api.notifications.read.all")),
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: QUERY_KEY });

            const previousNotifications = queryClient.getQueryData(QUERY_KEY);

            queryClient.setQueryData(QUERY_KEY, (old) => {
                return {
                    ...old,
                    pages: old.pages.map((page) => ({
                        ...page,
                        data: page.data.map((notification) => ({
                            ...notification,
                            read_at: new Date().toISOString(),
                        })),
                    })),
                };
            });

            return { previousNotifications };
        },
        onError: (err, _, context) => {
            queryClient.setQueryData(QUERY_KEY, context.previousNotifications);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        },
    });
}

async function fetchNotificationSettings() {
    const { data: r } = await axios.get(route("api.notifications.settings"));
    return r.data;
}

export function useNotificationSettings() {
    return useQuery({
        queryKey: SETTINGS_QUERY_KEY,
        queryFn: fetchNotificationSettings,
        refetchOnMount: true,
        select: (data) => {
            const types = {
                new_problem_or_upvote: [],
                problem_assigned: [],
                new_comment: [],
                tagged_in_comment: [],
            };
            Object.keys(types).forEach((key) => {
                types[key].push(
                    ...data.map((item) => {
                        return {
                            ...item,
                            settings: {
                                mail_notification_settings:
                                    item.settings.mail_notification_settings[
                                        key
                                    ],
                                push_notification_settings:
                                    item.settings.push_notification_settings[
                                        key
                                    ],
                                in_app_notification_settings:
                                    item.settings.in_app_notification_settings[
                                        key
                                    ],
                            },
                        };
                    })
                );
            });
            return types;
        },
    });
}

export function useUpdateNotificationSettings() {
    const queryClient = useQueryClient();
    const { t } = useI18n();

    return useMutation({
        mutationFn: ({ buildings, values }) =>
            axios.post(route("api.notifications.settings.update"), {
                data: parseData(buildings, values),
            }),
        onMutate: async () => {
            await queryClient.cancelQueries({
                queryKey: SETTINGS_QUERY_KEY,
            });

            const previousNotificationSettings = queryClient.getQueryData([
                "notifications",
                "settings",
            ]);

            return { previousNotificationSettings };
        },
        onError: (err, _, context) => {
            queryClient.setQueryData(
                SETTINGS_QUERY_KEY,
                context.previousNotificationSettings
            );
            toast.error(t("toasts.notifications.settings.update.error"));
        },
        onSuccess: () => {
            toast.success(t("toasts.notifications.settings.update.success"));
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: SETTINGS_QUERY_KEY,
            });
        },
    });
}

function parseData(buildings, values) {
    let data = buildings.map((building) => ({
        tenant_id: building.id,
        settings: {
            mail_notification_settings: {},
            push_notification_settings: {},
            in_app_notification_settings: {},
        },
    }));
    Object.keys(values).forEach((key) => {
        const settings = values[key];
        data.forEach((item, index) => {
            const buildingSettings = settings.find(
                (settings) => settings.tenant_id === item.tenant_id
            );
            data[index].settings.mail_notification_settings[key] =
                buildingSettings.settings.mail_notification_settings;
            data[index].settings.push_notification_settings[key] =
                buildingSettings.settings.push_notification_settings;
            data[index].settings.in_app_notification_settings[key] =
                buildingSettings.settings.in_app_notification_settings;
        });
    });
    return data;
}
